// Company-level defaults for a new shift (Task 24792).
//
// "Charge a placement / finders fee on completion" started as an off-by-default
// tick on every new shift. For an agency that bills every single shift, that is
// a box somebody has to remember on every shift they create - and the one they
// forget is the one that never gets invoiced. So the starting position is now a
// company setting.
//
// It is the ordinary three-tier config item (MasterConfigItem -> ConfigCompany
// -> ConfigUser), delivered to the client at login inside
// authUser.get_configitem_data as ZCONFIGVAL_57_X, and editable by the company
// on Settings -> Config Items because the master row carries
// FlagAllowFrontEndEdit = 1. FlagAllowUserOverride is 0: how a company bills is
// not a personal preference, so there is no per-staff override.
//
// WHY A WORD AND NOT 0/1: the config resolver treats '0' as unset
// (PHP empty('0') === true) and configcompany/append refuses an empty value, so
// a company that switched the default on with a '0'/'1' flag could never switch
// it back off - there would be no way to write the "off" value. Both states
// therefore carry a non-empty word, the same trick the feedback tool uses for
// ZCONFIGVAL_56_X.
//
// This only sets where a NEW shift starts. It never changes a saved shift, and
// it is never applied when opening an existing one for edit.

export const SHIFT_CHARGEABLE_CONFIG_ID = 57;
export const SHIFT_CHARGEABLE_CONFIG_KEY = "ZCONFIGVAL_57_X";

export const SHIFT_CHARGEABLE_YES = "yes";
export const SHIFT_CHARGEABLE_NO = "no";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "{}") || {};
  } catch (e) {
    return {};
  }
};

// Off unless the company has explicitly said yes, so nothing changes for a
// company that has not set it - and a missing or unseeded row can never start
// billing shifts on its own.
export const isShiftChargeableByDefault = () =>
  String(readAuthUser()?.get_configitem_data?.[SHIFT_CHARGEABLE_CONFIG_KEY] || "")
    .toLowerCase() === SHIFT_CHARGEABLE_YES;

export default isShiftChargeableByDefault;

// ---------------------------------------------------------------------------
// Task 24857 - the default FEE PRODUCT for a new shift.
//
// ZCONFIGVAL_57_X above only decides whether the box starts ticked. The product
// behind it - the thing that actually carries the VAT rate and the nominal the
// invoice line needs - still had to be picked by hand on every single shift.
// For an agency that bills the same placement fee every time, that is the same
// "remember it or it never gets invoiced" problem one level down.
//
// So there is a second company setting holding a Product.Id. When it is set:
//   * a NEW shift opens with that product already chosen, AND
//   * the chargeable box is ticked, regardless of 57 - choosing a fee product
//     company-wide is a statement that shifts are billed, so making the user
//     then also switch 57 on would just be a second place to forget.
// 57 still stands on its own for a company that wants the tick without
// committing to one product.
//
// Same "word, not a number" reasoning as above, for the same reason: '0' reads
// as unset and configcompany/append refuses an empty value, so a company that
// had picked a product could never go back to having none. SHIFT_FEE_PRODUCT_NONE
// is that written-down "no default".
//
// This only sets where a NEW shift starts. Opening a saved shift never has its
// product replaced by the company default.
export const SHIFT_FEE_PRODUCT_CONFIG_ID = 58;
export const SHIFT_FEE_PRODUCT_CONFIG_KEY = "ZCONFIGVAL_58_X";

export const SHIFT_FEE_PRODUCT_NONE = "none";

// The configured Product.Id as a string, or "" when the company has not set
// one. Anything that is not a positive whole number reads as "not set", so a
// stale or hand-typed value in the generic Config Items grid degrades to the
// old behaviour rather than prefilling a product id that does not exist.
export const getDefaultShiftFeeProductId = () => {
  const raw = String(
    readAuthUser()?.get_configitem_data?.[SHIFT_FEE_PRODUCT_CONFIG_KEY] || ""
  ).trim();
  if (!raw || raw.toLowerCase() === SHIFT_FEE_PRODUCT_NONE) return "";
  return /^\d+$/.test(raw) && Number(raw) > 0 ? raw : "";
};

// A company that has named a fee product is billing its shifts, so the tick
// follows the product. Falls back to the standalone 57 setting otherwise.
export const isShiftChargeableOnNewShift = () =>
  getDefaultShiftFeeProductId() !== "" || isShiftChargeableByDefault();

// Keep localStorage in step after saving the setting so the very next "New
// shift" uses it, instead of waiting for the next login (get_configitem_data is
// only rebuilt at login / company switch).
export const patchShiftDefaults = (patch) => {
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser")) || {};
    authUser.get_configitem_data = {
      ...(authUser.get_configitem_data || {}),
      ...patch,
    };
    localStorage.setItem("authUser", JSON.stringify(authUser));
  } catch (e) {
    /* localStorage unavailable - the value still resolves on next login */
  }
};
